'use client';
import { useInfiniteScroll } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { AgentLineModel } from '@/app/components/icons/AgentLineModel';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/app/components/ui/dialog';
import { SEARCH_EMPTY_ICON } from '@/app/constants';
import { cn } from '@/app/utils';
import type { ModelOption } from '../../_types';
import { agentListModels } from '../../apis';
import { PAGE_SIZE } from '../../constants';
import { LimitedTag } from '../LimitedTag';

type GenderType = 'male' | 'female' | 'all';

type ModelListData = {
  list: ModelOption[];
  nextPage: number;
};

const getModelList: ({ gender, page }: { gender?: GenderType; page?: number }) => Promise<ModelListData> = ({
  gender,
  page = 1,
}) =>
  new Promise((res) => {
    agentListModels({
      query: {
        gender: gender === 'all' ? undefined : gender,
        'pager.page': page,
        'pager.pagesz': PAGE_SIZE,
      },
    })
      .then(({ data, error }) => {
        if (error) {
          res({
            list: [],
            nextPage: page + 1,
          });
        } else {
          const list: ModelOption[] = (data.models || []).map((it) => {
            return {
              id: v4(),
              name: it.name,
              gender: it.gender && it.gender !== 'unspecified' ? it.gender : 'male',
              description: it.description || '',
              img: it.avatar_url,
            };
          });

          const hasMore = (list?.length || 0) >= PAGE_SIZE;

          res({
            list,
            nextPage: hasMore ? page + 1 : 1,
          });
        }
      })
      .catch((_e) => {
        res({
          list: [],
          nextPage: 1,
        });
      });
  });

const ModelItem: FC<{
  model: ModelOption;
  onSelectModel: (model: ModelOption) => void;
}> = (props) => {
  const { model, onSelectModel } = props;
  return (
    <div
      className="flex w-[130px] flex-col items-center justify-center rounded-12 p-4 hover:bg-system-white12"
      onClick={() => {
        onSelectModel(model);
      }}
    >
      <img src={model.img} alt={model.name} className="h-20 w-20 rounded-full object-cover" />
      <div className="mt-2 truncate text-sm text-system-white leading-[1.6]">{model.name}</div>
    </div>
  );
};

export const ModelSelector = ({ onSelectModel }: { onSelectModel: (model: ModelOption) => void }) => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectOptions: { name: string; id: GenderType }[] = useMemo(() => {
    return [
      {
        name: t('elements.allTag'),
        id: 'all',
      },
      {
        name: t('agentProject.gender.male'),
        id: 'male',
      },
      {
        name: t('agentProject.gender.female'),
        id: 'female',
      },
    ];
  }, []);

  const [gender, setGender] = useState<GenderType>('all');

  const {
    data: listData,
    loading: firstLoading,
    reload,
  } = useInfiniteScroll<ModelListData>((d) => getModelList({ gender, page: d?.nextPage }), {
    target: ref,
    manual: true,
    isNoMore: (d) => (d?.nextPage ?? 1) === 1,
    reloadDeps: [gender],
  });

  useEffect(() => {
    isOpen && reload();
  }, [isOpen]);

  const handleSelectModel = (model: ModelOption) => {
    onSelectModel(model);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <CustomTooltip
          content={t('agentProject.modelSelect.tip')}
          side="top"
          sideOffset={4}
          disableHoverableContent={true}
          variant="glass"
        >
          <div className="flex h-8 items-center justify-between rounded-full border border-system-white12 bg-system-white04 p-2 text-system-white transition-colors hover:border-system-white64 hover:bg-system-white24">
            <AgentLineModel className="size-4" />
          </div>
        </CustomTooltip>
      </DialogTrigger>

      <DialogContent
        className="flex h-[532px] w-[600px] flex-col gap-3 overflow-hidden rounded-16 border-system-white16 bg-[rgba(34,37,42,0.64)] p-4 backdrop-blur-[15px] [box-shadow:0_10px_20px_0_rgba(0,0,0,0.08)]"
        closeClassName="top-4 right-4"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-semibold text-base leading-[1.4]">
            {t('agentProject.modelSelect.title')}
            <LimitedTag />
          </DialogTitle>
        </DialogHeader>
        <div className="flex h-[38px] flex-shrink-0 items-center rounded-8 bg-system-white04">
          {selectOptions.map((it) => (
            <div
              key={it.id}
              className={cn(
                'h-full flex-1 cursor-pointer rounded-8 border border-transparent py-2 text-center text-sm',
                gender === it.id && 'border-system-white64 bg-system-white24',
              )}
              onClick={() => setGender(it.id)}
            >
              {it.name}
            </div>
          ))}
        </div>

        {firstLoading ? (
          <div className="relative flex flex-1 items-center justify-center">
            <GlobalLoader className="top-1/2" />
          </div>
        ) : (listData?.list?.length || 0) > 0 ? (
          <div className="hide-scrollbar mt-3 grid flex-1 grid-cols-4 gap-4 overflow-y-scroll" ref={ref}>
            {listData?.list?.map((model) => (
              <div key={model.id} className="cursor-pointer last:mb-0">
                <ModelItem model={model} onSelectModel={handleSelectModel} />
              </div>
            ))}
          </div>
        ) : (
          <SearchEmpty
            emptySrc={SEARCH_EMPTY_ICON.selector}
            textClassName="text-xs"
            emptyText={t('agentProject.noRecord')}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
